import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
	checkRole,
	dataCheckedByOptions,
	dataCheckedOptions,
	entryStatusOptions, pressOfficeEntryStatusOptions,
	qualifiedStatusOptions,
	selectedOptions
} from '../utils/Helpers';
import {searchUsers} from "../api/user";

const EntryFilter = ({ handleFilters, filters }) => {
	const [status, setStatus] = useState(null);
	const [selected, setSelected] = useState(null);
	const [qualified, setQualified] = useState(null);
	const [event, setEvent] = useState(null);
	const [dataCheck, setDataCheck] = useState(null);
	const [dataCheckBy, setDataCheckBy] = useState({proofread: false, analyst: false, steward: false});
	const [archivedOnly, setArchivedOnly] = useState(null);
	const [events, setEvents] = useState(null);
	const [adminUsers, setAdminUsers] = useState(null);

	const user = useSelector(state => state.user);
	const isPressOffice = checkRole(user.user.role, 'press-office', true);

	const eventsList = useSelector((state) => state.events);
	const location = useLocation();
	const navigate = useNavigate();

	const getEntryStatusOptions = isPressOffice ? pressOfficeEntryStatusOptions : entryStatusOptions;

	useEffect(() => {
		fetchAdminUsers();
		mapEventsToSelect();
	}, []);

	useEffect(() => {
		setFiltersFromURL();
	}, [events, adminUsers]);

	const fetchAdminUsers = async () => {
		try {
			const data = {
				role: 'admin,regatta-admin',
				term: '',
			}

			const response = await searchUsers(data);
			if (response.status === 200) {
				const adminOptions = [{ value: '', label: 'All' }];

				Object.entries(response.data.data).forEach(([key, user]) => {
					adminOptions.push({ value: user.id, label: `${user.first_name} ${user.last_name}` });
				});

				setAdminUsers(adminOptions);
			}
		} catch (error) {
			console.log('error', error);
		}
	}

	const mapEventsToSelect = () => {
		const eventOptions = [{ value: '', label: 'All' }];
		Object.entries(eventsList.events).forEach(([key, value]) => {
			eventOptions.push({ value: value.id, label: value.name });
		});
		setEvents(eventOptions);
	};

	const findOptionLabel = (options, value) => {
		if (!options) return value;
		const intValue = isNaN(value) ? value : parseInt(value, 10);
		const option = options.find((option) => option.value === intValue);
		return option ? option.label : value;
	};

	const getFilterState = (options, value) => {
		if (!value) return null;
		return { value, label: findOptionLabel(options, value) };
	};

	const getFilterStateForCheckbox = (options, value) => {
		let dataCheckedBy = {proofread: false, analyst: false, steward: false};

		if (!value) return dataCheckedBy;

		const checkedBoxes = value.split(',');

		options.map((option) => {
			if(checkedBoxes.includes(option.value)) {
				dataCheckedBy[option.value] = true;
			}
		})

		return dataCheckedBy;
	}

	const setFiltersFromURL = () => {
		const searchParams = new URLSearchParams(location.search);
		const newFilters = {
			status: getFilterState(getEntryStatusOptions, searchParams.get('status')),
			selected: getFilterState(selectedOptions, searchParams.get('selected')),
			qualified: getFilterState(qualifiedStatusOptions, searchParams.get('qualified')),
			event: getFilterState(events, searchParams.get('event')),
			archivedOnly: getFilterState(adminUsers, searchParams.get('archivedOnly')),
			dataCheck: getFilterState(dataCheckedOptions, searchParams.get('dataCheck')),
			dataCheckBy: getFilterStateForCheckbox(dataCheckedByOptions, searchParams.get('dataCheckBy')),
		};
		setStatus(newFilters.status);
		setSelected(newFilters.selected);
		setQualified(newFilters.qualified);
		setEvent(newFilters.event);
		setArchivedOnly(newFilters.archivedOnly);
		setDataCheck(newFilters.dataCheck);
		setDataCheckBy(newFilters.dataCheckBy);
		handleFilters(newFilters);
	};

	const updateURLParams = (newFilters) => {
		const searchParams = new URLSearchParams(location.search);
		Object.keys(newFilters).forEach((key) => {
			if(key === 'dataCheckBy') {
				const dataCheckValue = urlEncodedDataCheck(newFilters[key]);
				if (dataCheckValue) {
					searchParams.set(key, dataCheckValue);
				} else {
					searchParams.delete(key);
				}
				return;
			}

			if (newFilters[key]) {
				searchParams.set(key, newFilters[key].value);
			} else {
				searchParams.delete(key);
			}
		});
		navigate({ search: searchParams.toString() });
	};

	const handleStatusChange = (selectedOption) => {
		const newStatus = selectedOption.value === '' ? null : selectedOption;
		setStatus(newStatus);
		const newFilters = { status: newStatus, selected, event, qualified, dataCheck, dataCheckBy, archivedOnly };
		handleFilters(newFilters);
		updateURLParams(newFilters);
	};

	const handleSelectedChange = (selectedOption) => {
		const newSelected = selectedOption.value === '' ? null : selectedOption;
		setSelected(newSelected);
		const newFilters = { status, selected: newSelected, event, qualified, dataCheck, dataCheckBy, archivedOnly };
		handleFilters(newFilters);
		updateURLParams(newFilters);
	};

	const handleEventChange = (selectedOption) => {
		const newEvent = selectedOption.value === '' ? null : selectedOption;
		setEvent(newEvent);
		const newFilters = { status, selected, event: newEvent, qualified, dataCheck, dataCheckBy, archivedOnly };
		handleFilters(newFilters);
		updateURLParams(newFilters);
	};

	const handleQualifiedChange = (selectedOption) => {
		const newQualified = selectedOption.value === '' ? null : selectedOption;
		setQualified(newQualified);
		const newFilters = { status, selected, event, qualified: newQualified, dataCheck, dataCheckBy, archivedOnly };
		handleFilters(newFilters);
		updateURLParams(newFilters);
	};

	const handleArchivedOnlyChange = (e) => {
		let newArchivedOnly = {}
		if (!e.target.checked) {
			newArchivedOnly = null
		} else {
			newArchivedOnly.value = 1;
		}
		setArchivedOnly(newArchivedOnly);
		const newFilters = { status, selected, event, qualified, dataCheck, dataCheckBy, archivedOnly: newArchivedOnly };
		handleFilters(newFilters);
		updateURLParams(newFilters);
	}

	const handleDataCheckChange = (selectedOption) => {
		const newDataCheck = selectedOption.value === '' ? null : selectedOption;

		let newDataCheckedBy = dataCheckBy;

		if (newDataCheck === null) {
			newDataCheckedBy = {proofread: false, analyst: false, steward: false};
		}

		setDataCheckBy(newDataCheckedBy);
		setDataCheck(newDataCheck);
		const newFilters = { status, selected, event, qualified, dataCheck: newDataCheck, dataCheckBy: newDataCheckedBy, archivedOnly };
		handleFilters(newFilters);
		updateURLParams(newFilters);
	};

	const handleDataCheckByChange = (key, value) => {
		const updatedState = {
			...dataCheckBy,
			[key]: value,
		}

		setDataCheckBy(updatedState);

		const newFilters = { status, selected, event, qualified, dataCheck, dataCheckBy: updatedState, archivedOnly };
		handleFilters(newFilters);
		updateURLParams(newFilters)
	}

	const urlEncodedDataCheck = (data) => {
		let selectedKeys = [];
		Object.entries(data).forEach(([key, value]) => {
			if (value) {
				selectedKeys.push(key);
			}
		});
		return selectedKeys.join(',');
	};

	return (
		<div className="filter-container row">
			<div className="filter-item col-12 col-md-6 mb-3">
				<label>Entry Status</label>
				<Select value={filters.status} onChange={handleStatusChange} options={getEntryStatusOptions}/>
			</div>
			<div className="filter-item col-12 col-md-6 mb-3">
				<label>Selected</label>
				<Select value={filters.selected} onChange={handleSelectedChange} options={selectedOptions}/>
			</div>
			<div className="filter-item col-12 col-md-6 mb-3">
				<label>Qualified Status</label>
				<Select value={filters.qualified} onChange={handleQualifiedChange} options={qualifiedStatusOptions}/>
			</div>
			<div className="filter-item col-12 col-md-6 mb-3">
				<label>Event</label>
				<Select value={filters.event} onChange={handleEventChange} options={events ?? []}/>
			</div>
			<div className="filter-item col-12 col-md-6 mb-3">
				<label>Data checked</label>
				<Select value={filters.dataCheck} onChange={handleDataCheckChange} options={dataCheckedOptions}/>

				{!isNaN(dataCheck?.value) && (
					dataCheckedByOptions.map((option) => (
						<div key={option.value} className="form-check form-check-inline mt-2">
							<input
								className="form-check-input ms-1"
								type="checkbox"
								id={`data-checked-by-${option.value}`}
								checked={dataCheckBy?.[option.value] || false}
								onChange={(e) => handleDataCheckByChange(option.value, e.target.checked)}
							/>
							<label className="form-check-label" htmlFor={`data-checked-by-${option.value}`}>
								{option.label}
							</label>
						</div>
					))
				)}
			</div>
			<div className="filter-item col-12 col-md-6 mb-3">
				<div className="d-flex form-switch">
					<input className="form-check-input"
						   type="checkbox"
						   id="archived_only"
						   checked={filters.archivedOnly?.value ?? false}
						   onChange={(event) => handleArchivedOnlyChange(event)}
					/>
					<label className="form-check-label" htmlFor="archived_only">Archived Records Only</label>
				</div>
			</div>
		</div>
	);
};

export default EntryFilter;
